import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { Page, Button, Radio, Input, Modal, Select, alert } from 'equasion-lib';
import {
  FiAlertTriangle,
  FiBox,
  FiLoader,
  FiPlus,
  FiShoppingBag,
  FiTrash,
  FiXCircle
} from 'react-icons/fi';
import './SalesOrderShipping.scss';
import { useRootStore } from '@/lib/providers/RootProvider';
import { salesOrdersTabPath } from '@/lib/pickVerifyShip';
import { initialManualShippingCostDollars } from './manualShipCost';
import PackageSizeField from './PackageSizeField';
import {
  changePackageType,
  dimensionKey,
  formatDimensions,
  newPackage,
  packingDimensions,
  packageShipInput,
  packagesNeedingSize,
  savedSizesFor,
  validateCustomDimensions,
  type ShipPackageDraft
} from './packageSize';
import {
  scaleWeight,
  useLaneScaleReading,
  type WarehouseShippingLane
} from '@/lib/shippingLane';
import {
  SLOW_SHIP_SECONDS,
  clearShipRun,
  getShipRun,
  restorableShipDraft,
  startShipRun,
  useElapsedSeconds,
  useShipRun,
  watchShipRun,
  type ShipDraft,
  type ShipRun
} from '@/lib/shipRun';
import {
  HOME_PATH,
  SHIPPED_NOTICE_SECONDS,
  SHIP_ORDER_STATUS,
  afterShipFor,
  shippedNotice,
  useShipAutoPrint
} from '@/lib/shipHandoff';
import { recordRecentShipment } from '@/lib/recentShipments';

/** Where a started ship sends the operator: straight to its documents, which stream in there. */
export const shipDocumentsLocation = (orderId: string, bundleId?: string | null) => ({
  pathname: `/sales-order-documents/${orderId}`,
  search: bundleId ? `?shipping=true&bundle=${bundleId}` : '?shipping=true'
});

/**
 * A ship from this page that did not go through, shown above the ship button with a way to retry.
 * `outcomeUnknown`: the answer never arrived (dropped wifi, a gateway timeout), so the page checks
 * the order for a while before it lets the operator try again — the server may have shipped it.
 */
type ShipProblem = {
  run: ShipRun;
  message: string;
  outcomeUnknown: boolean;
  /** The unknown outcome's check window ran out with the order still not shipped. */
  checkTimedOut?: boolean;
};

/** How long a lost answer is checked before Try Again is offered. A ship takes seconds. */
export const UNKNOWN_OUTCOME_CHECK_MS = 60 * 1000;
const UNKNOWN_OUTCOME_POLL_MS = 1500;
/** A status read older than this is presumed wedged (the HTTP link has no timeout). */
const STATUS_READ_DEADLINE_MS = 10 * 1000;

/** What the operator had entered, if their last ship of this order FAILED (else undefined). */
const failedShipDraft = (orderId?: string): ShipDraft | undefined =>
  restorableShipDraft(getShipRun(orderId), orderId);

// Warehouse is on react-icons 4.x; RiArchiveStackLine only shipped in 5.x. Same Remix path.
const RiArchiveStackLine = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em" {...props}>
    <path d="M4 5H20V3H4V5ZM20 9H4V7H20V9ZM3 11H10V13H14V11H21V20C21 20.5523 20.5523 21 20 21H4C3.44772 21 3 20.5523 3 20V11ZM16 13V15H8V13H5V19H19V13H16Z" />
  </svg>
);

/**
 * Stop Enter from committing a numeric Input's value. The Input's own onKeyDown coerces the field
 * to `+target.value` on Enter — which turns an empty field into a real 0 — and unlike its blur
 * handler that path is NOT covered by the `manual` prop.
 */
const suppressEnterCommit = (e) => {
  if (e.key === 'Enter' || e.keyCode === 13) e.preventDefault();
};

/** Keep in sync with `DEA_SCHEDULE_LEVELS` in libs/db fda-product.model (schedules that require serialization/EPCIS). */
// Rx Exempt is unserialized — do NOT add it here.
const DEA_SCHEDULE_LEVELS = ['Rx Drugs', 'CV', 'CIV', 'CIII', 'CII', 'CI'];

/**
 * Above the ship button: the ship that is running (when its labels print by themselves the operator
 * waits here, not on the Documents page), or why the last one did not go through. Nothing when
 * there is neither.
 */
const ShipStatus = React.forwardRef<
  HTMLDivElement,
  { running?: ShipRun; problem: ShipProblem | null; onCheckAgain: () => void }
>(({ running, problem, onCheckAgain }, ref) => {
  const elapsed = useElapsedSeconds(running?.startedAt, !!running);

  if (running) {
    return (
      <div ref={ref} className="ship-status is-running" role="status">
        <div className="ship-status-head">
          <FiLoader className="ship-in-flight-spinner" aria-hidden="true" />
          <h3>
            {running.buysCarrierLabel
              ? `Generating labels for ${running.label}`
              : `Shipping ${running.label}`}
          </h3>
        </div>
        <p className="ship-status-note">
          {running.buysCarrierLabel
            ? 'They print as soon as the shipment is recorded. '
            : 'Its documents print once the shipment is recorded. '}
          You go back to the home screen when it&apos;s done.
        </p>
        {elapsed >= SLOW_SHIP_SECONDS && (
          <p className="ship-status-note">
            Still working ({elapsed}s) — the carrier is slow to answer. This keeps going even if you
            leave this page.
          </p>
        )}
      </div>
    );
  }

  if (!problem) return null;
  const { run } = problem;
  const what = run.buysCarrierLabel ? 'labels' : 'the shipment';

  if (problem.outcomeUnknown) {
    return (
      <div
        ref={ref}
        className={['ship-status', problem.checkTimedOut ? 'is-failed' : 'is-pending'].join(' ')}
        role={problem.checkTimedOut ? 'alert' : 'status'}
      >
        <div className="ship-status-head">
          {problem.checkTimedOut ? (
            <FiAlertTriangle aria-hidden="true" />
          ) : (
            <FiLoader className="ship-in-flight-spinner" aria-hidden="true" />
          )}
          <h3>
            {problem.checkTimedOut
              ? `${run.label} has not shipped`
              : `Lost the connection while generating ${what}`}
          </h3>
        </div>
        <p className="ship-status-note">
          {problem.checkTimedOut
            ? 'If nobody else is shipping it, tap Try Again.'
            : `Checking whether ${run.label} went through — this page moves on by itself if it did.`}
        </p>
        {problem.checkTimedOut && (
          <Button type="secondary" fullWidth onClick={onCheckAgain}>
            Check Again
          </Button>
        )}
      </div>
    );
  }

  return (
    <div ref={ref} className="ship-status is-failed" role="alert">
      <div className="ship-status-head">
        <FiAlertTriangle aria-hidden="true" />
        <h3>{run.buysCarrierLabel ? 'Labels were not generated' : `${run.label} was not shipped`}</h3>
      </div>
      <p className="ship-status-error">{problem.message}</p>
      <p className="ship-status-note">
        Nothing was shipped. Fix the problem, then tap Try Again.
        {run.buysCarrierLabel
          ? ' A label already bought for this order is reused, not bought again.'
          : ''}
      </p>
    </div>
  );
});
ShipStatus.displayName = 'ShipStatus';

const SalesOrderShipping = ({
  salesOrder,
  bundleContext,
  shippingDimensionOptions,
  shippingMethods,
  orderShip,
  bundleShip,
  addShippingSize,
  vendorShippingLocations,
  updateVendorShippingLocation,
  fobEnabled = false,
  shippingLane = null as WarehouseShippingLane | null
}) => {
  const vendorId = useRootStore((state) => state.selectedCompany.id);
  const submittingRef = useRef(false);
  const [gcpLoading, setGcpLoading] = useState(false);
  const navigate = useNavigate();
  const {
    id: salesOrderId,
    vendorSalesOrderNumber: salesOrderNumber,
    orderItems,
    shippingMethod
  } = salesOrder || {};
  // Back here after a ship that FAILED: put back what the operator had entered. Read at mount for
  // the first render, and again by the effect below if the ship fails while this page is open.
  const [restoredDraft] = useState(() => failedShipDraft(salesOrderId));
  const [packages, setPackages] = useState<ShipPackageDraft[]>(
    () => (restoredDraft?.packages as ShipPackageDraft[]) || []
  );
  const [selectedPackageIndex, setSelectedPackageIndex] = useState(null);
  // Set when Save is pressed on a package whose size is not usable yet: every size error shows.
  const [sizeErrorsShown, setSizeErrorsShown] = useState(false);
  // Adding a typed size to the package library: in flight, and why the last try failed.
  const [addingSize, setAddingSize] = useState(false);
  const [addSizeError, setAddSizeError] = useState<string | null>(null);
  // Sizes this page added to the library. The add's response updates the cached company too; this
  // keeps a just-added size listed (and its package shippable) whatever the cache does.
  const [addedSizes, setAddedSizes] = useState<number[][]>([]);
  const dimensionOptions = useMemo(
    () => [
      ...(Array.isArray(shippingDimensionOptions) ? shippingDimensionOptions : []),
      ...addedSizes
    ],
    [shippingDimensionOptions, addedSizes]
  );
  const [shippingMethodId, setShippingMethodId] = useState(
    () => (restoredDraft?.shippingMethodId as string) || shippingMethod?.id
  );
  // A ship of this order already in flight from this device (the operator came Back from the
  // Documents page mid-ship). Starting another could race the first, so the page points back there.
  const activeRun = useShipRun(salesOrderId);
  const shipInFlight = activeRun?.status === 'running';
  // …and one that has since SUCCEEDED: the order is shipped, so this form must not ship it again.
  const shipDone = activeRun?.status === 'succeeded';
  // A ship that goes home once it succeeds waits HERE (see @/lib/shipHandoff): the page shows its
  // progress in place of the Documents page's.
  const waitingHere = shipInFlight && activeRun?.afterShip === 'home';
  const [shipProblem, setShipProblem] = useState<ShipProblem | null>(null);
  const checkingOutcome = !!shipProblem?.outcomeUnknown && !shipProblem.checkTimedOut;
  // Nothing on the form may change while a ship of it is out, being checked, or done.
  const formLocked = shipInFlight || shipDone || checkingOutcome;
  const apolloClient = useApolloClient();
  const statusRef = useRef<HTMLDivElement>(null);
  // This page shows its order's ship outcome itself, so the run store must not toast over it.
  useEffect(() => (salesOrderId ? watchShipRun(String(salesOrderId)) : undefined), [salesOrderId]);
  // BUNDLED SHIPPING. `bundleContext.bundle` is present only when THIS order is a member, so it is
  // the single switch between the ordinary per-order ship and the bundle ship. A one-member bundle
  // still ships through the bundle path — the server treats it exactly like a solo ship, and
  // routing it the same way keeps the bundle record consistent (it flips to `shipped`).
  const activeBundle = bundleContext?.bundle || null;
  const bundleOrderCount = activeBundle?.orders?.length || 0;
  const isBundleShip = !!activeBundle;
  // FOB (buyer-arranged) order: no carrier method on the order AND the vendor allows FOB. Shipped
  // alone, the warehouse records a manual freight cost + 3rd-party tracking number.
  const isFobOrder = !shippingMethod?.id && fobEnabled;
  // FREIGHT (LTL/pallet) order: keyed on the ORDER'S OWN method carrier, not the company-wide FOB
  // flag — a freight order HAS a shippingMethodId, so `isFobOrder` is always false for one. This
  // mirrors the server, which resolves the method from the order and branches on its carrier.
  const isFreightOrder = shippingMethod?.carrier === 'freight';
  // A bundle ships on one carrier label. The method selected below is written onto every member,
  // including an FOB order that was placed with none. Freight still cannot share that label.
  const bundleCarrierShip = isBundleShip && !isFreightOrder;
  // Solo FOB and freight ship without buying a carrier label: cost + tracking are entered by hand.
  const isManualShip = !bundleCarrierShip && (isFobOrder || isFreightOrder);
  // Freight edited onto a member after it was set aside cannot share the carton. Say so and offer
  // the one way out (remove it) instead of a doomed Ship button.
  const bundledButManual = isBundleShip && isFreightOrder;
  // Orders combined at the end of Pick or at Verify join the bundle before they are packed. The
  // carton ships once every one of them is verified (the server refuses earlier, naming the order).
  const bundleNotReady = (activeBundle?.orders || []).filter(
    (order) => order.vendorStatus !== 'shipping'
  );
  // Prefill from the amount already on the SO (freight quote / prior edit). Field stays editable.
  const [fobShippingCost, setFobShippingCost] = useState(() =>
    restoredDraft && 'fobShippingCost' in restoredDraft
      ? (restoredDraft.fobShippingCost as any)
      : initialManualShippingCostDollars(salesOrder?.shippingAmount, { isFreightOrder }),
  );
  const [fobTrackingNumber, setFobTrackingNumber] = useState(
    () => (restoredDraft?.fobTrackingNumber as string) || ''
  );
  const [freightWeight, setFreightWeight] = useState<any>(
    () => (restoredDraft?.freightWeight as any) ?? ''
  );
  // A numeric Input reports a NUMBER once it holds a value, and the empty string when cleared, so
  // these fields are string|number. Normalize before any string operation.
  const freightCostText = String(fobShippingCost ?? '').trim();
  const freightWeightText = String(freightWeight ?? '').trim();
  const defaultVendorShippingLocation =
    vendorShippingLocations.find((location) => location.isDefaultSalesOrder) ||
    vendorShippingLocations[0];
  const [vendorShippingLocationId, setVendorShippingLocationId] = useState(
    () =>
      (restoredDraft?.vendorShippingLocationId as string) ||
      salesOrder?.metadata?.vendorShippingLocation?.id ||
      defaultVendorShippingLocation?.id
  );

  // A failed ship of this order — at mount, or while the operator waits here — gives the form back
  // as it was entered, says why above the ship button, then is forgotten (Try Again starts a new one).
  useEffect(() => {
    if (activeRun?.status !== 'failed') return;
    const draft = restorableShipDraft(activeRun, salesOrderId);
    if (draft) {
      setShipProblem({
        run: activeRun,
        message: activeRun.error || 'The shipment was not recorded.',
        outcomeUnknown: !!activeRun.outcomeUnknown
      });
      if (Array.isArray(draft.packages)) setPackages(draft.packages as ShipPackageDraft[]);
      if (draft.shippingMethodId) setShippingMethodId(draft.shippingMethodId as string);
      if (draft.vendorShippingLocationId) {
        setVendorShippingLocationId(draft.vendorShippingLocationId as string);
      }
      if ('fobShippingCost' in draft) setFobShippingCost(draft.fobShippingCost as any);
      if ('fobTrackingNumber' in draft) setFobTrackingNumber(draft.fobTrackingNumber as string);
      if ('freightWeight' in draft) setFreightWeight(draft.freightWeight as any);
    }
    clearShipRun(salesOrderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRun?.status]);

  // Whether this ship's carton labels print by themselves from the location selected below.
  const shipAutoPrint = useShipAutoPrint(
    vendorId as string,
    salesOrderId,
    vendorShippingLocationId
  );

  // The order went out: off to the home screen with the confirmation (or, when its labels do not
  // print by themselves, to its Documents page). Once per visit.
  const finishedRef = useRef(false);
  const finishShipped = (run: ShipRun) => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    if (run.afterShip === 'home') {
      const notice = shippedNotice(run, true);
      alert(notice.title, notice.message, SHIPPED_NOTICE_SECONDS, 'success');
      // Replace: Back from home must not reopen the form of an order that has shipped.
      navigate(HOME_PATH, { replace: true });
    } else {
      navigate(shipDocumentsLocation(salesOrderId, run.kind === 'bundle' ? activeBundle?.id : null));
    }
  };
  // Only a ship that finishes while this page is open moves the operator on. Reopening an order
  // whose ship already went through (it was left mid-ship, and toasted when it finished) just says
  // it has shipped.
  const [openedAt] = useState(() => Date.now());
  useEffect(() => {
    if (
      activeRun?.status === 'succeeded' &&
      activeRun.afterShip === 'home' &&
      (activeRun.finishedAt ?? 0) >= openedAt
    ) {
      finishShipped(activeRun);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRun?.status]);

  // The answer to a ship was lost: check the order until it says shipped (then carry on as a success
  // would) or the window runs out (then Try Again is offered).
  useEffect(() => {
    if (!shipProblem?.outcomeUnknown || shipProblem.checkTimedOut) return undefined;
    const { run } = shipProblem;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const startedAt = Date.now();
    const readStatus = () =>
      Promise.race([
        apolloClient.query({
          query: SHIP_ORDER_STATUS,
          variables: { id: salesOrderId, vendorId },
          fetchPolicy: 'network-only',
          context: { queryDeduplication: false }
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('timed out')), STATUS_READ_DEADLINE_MS)
        )
      ]) as Promise<any>;
    const tick = async () => {
      try {
        const { data } = await readStatus();
        if (cancelled) return;
        const status = data?.salesOrder?.vendorStatus;
        if (status === 'shipped' || status === 'customer_pickup' || status === 'completed') {
          recordRecentShipment(vendorId as string, {
            orderId: run.orderId,
            label: run.label,
            orderCount: run.orderIds.length,
            printed: run.afterShip === 'home'
          });
          setShipProblem(null);
          finishShipped(run);
          return;
        }
      } catch {
        // Still offline, or a slow read: the next tick tries again.
      }
      if (cancelled) return;
      if (Date.now() - startedAt >= UNKNOWN_OUTCOME_CHECK_MS) {
        setShipProblem((problem) => (problem ? { ...problem, checkTimedOut: true } : problem));
        return;
      }
      timer = setTimeout(tick, UNKNOWN_OUTCOME_POLL_MS);
    };
    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipProblem?.outcomeUnknown, shipProblem?.checkTimedOut, shipProblem?.run]);

  // A problem arriving is the thing to look at: bring it into view above the button.
  useEffect(() => {
    if (shipProblem) statusRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [shipProblem?.run, shipProblem?.checkTimedOut]);

  const ensureVendorShippingLocationOnOrder = async () => {
    if (!vendorShippingLocationId) return;
    if (vendorShippingLocationId === salesOrder?.metadata?.vendorShippingLocation?.id)
      return;
    await updateVendorShippingLocation({
      variables: {
        orderId: salesOrderId,
        companyId: vendorId,
        vendorShippingLocationId
      }
    });
  };
  const totalQuantity = orderItems.reduce((a, { quantity }) => a + quantity, 0);
  const hasSerializedProducts = orderItems.some(({ product }) => {
    const { fdaProduct, upcProduct } = product || {};
    return !upcProduct && DEA_SCHEDULE_LEVELS.includes(fdaProduct?.deaSchedule);
  });

  const returnToShipping = () => navigate(salesOrdersTabPath('shipping'));

  const handleGcpChange = async ({ value: locationId }) => {
    setVendorShippingLocationId(locationId);
    try {
      setGcpLoading(true);
      await updateVendorShippingLocation({
        variables: {
          orderId: salesOrderId,
          companyId: vendorId,
          vendorShippingLocationId: locationId
        }
      });
    } catch (e) {
      console.log(`ERROR UPDATING VENDOR SHIPPING LOCATION: ${e}`);
    }
    setGcpLoading(false);
  };

  const selectedPackage = packages[selectedPackageIndex];
  const { type, weight, weightEntered, weightKept } = selectedPackage || ({} as ShipPackageDraft);
  // Packages whose size cannot buy a label yet (a custom size left blank or invalid).
  const packagesWithoutSize = packagesNeedingSize(packages);

  const updatePackage = (patch) => {
    let packagesCopy = [...packages];
    packagesCopy[selectedPackageIndex] = { ...selectedPackage, ...patch };
    setPackages(packagesCopy);
  };
  const updatePackages = (key, value) => updatePackage({ [key]: value });

  // The user's shipping lane scale fills the open package's weight until they type one in. Closing
  // the package keeps the weighed value, so reopening it later leaves the weight alone.
  const laneScaleActive = !!shippingLane?.hasScale && !!selectedPackage;
  const laneScaleLive = laneScaleActive && !weightEntered && !weightKept;
  const laneScale = useLaneScaleReading(vendorId as string, laneScaleLive);
  useEffect(() => {
    if (!laneScaleLive || laneScale.pounds == null) return;
    const scaled = scaleWeight(laneScale.pounds);
    if (scaled !== weight) updatePackages('weight', scaled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laneScaleLive, laneScale.pounds, selectedPackageIndex]);
  const laneScaleName = shippingLane ? `${shippingLane.label} scale` : '';
  const closePackage = () => {
    if (laneScaleLive && laneScale.pounds != null) updatePackage({ weightKept: true });
    setSelectedPackageIndex(null);
  };
  const openPackage = (index) => {
    if (formLocked) return;
    setSizeErrorsShown(false);
    setAddSizeError(null);
    setSelectedPackageIndex(index);
  };
  /**
   * A size typed into the sheet goes into the vendor's package library, then the package uses it
   * like any saved size. Already saved (in any orientation) → the saved one is selected.
   */
  const addTypedSize = async (index: number, pkg: ShipPackageDraft) => {
    const typed = validateCustomDimensions(pkg.custom, pkg.type).dimensions;
    if (!typed) {
      setSizeErrorsShown(true);
      return;
    }
    setAddingSize(true);
    setAddSizeError(null);
    try {
      const { data } = await addShippingSize({
        variables: { companyId: vendorId, dimensions: typed }
      });
      const result = data?.companyShippingDimensionOptionAdd;
      const saved: number[] = (result?.dimensions || typed).map(Number);
      setAddedSizes((sizes) => [...sizes, saved]);
      setPackages((current) =>
        current.map((p, j) =>
          j === index ? { ...p, sizeMode: 'saved', size: dimensionKey(saved) } : p
        )
      );
      alert(
        result?.added === false ? 'Already a saved size' : 'Added to saved sizes',
        result?.added === false
          ? `Using ${formatDimensions(saved)}.`
          : `${formatDimensions(saved)} is in the size list from now on.`,
        4,
        'success'
      );
      setSelectedPackageIndex((open) => (open === index ? null : open));
    } catch (e: any) {
      setAddSizeError(
        e?.graphQLErrors?.[0]?.message ||
          (e?.networkError
            ? 'No connection — the size was not saved. Try again.'
            : 'The size could not be saved. Try again.')
      );
    } finally {
      setAddingSize(false);
    }
  };
  // Save keeps the sheet open, with the reason on screen, until the size is usable. (Dismissing
  // the sheet still closes it — the package card then says it needs a size, and Generate Labels
  // waits for it.)
  const savePackage = () => {
    if (addingSize) return;
    if (selectedPackage?.sizeMode === 'custom') {
      addTypedSize(selectedPackageIndex, selectedPackage);
      return;
    }
    if (!packingDimensions(selectedPackage)) {
      setSizeErrorsShown(true);
      return;
    }
    setSelectedPackageIndex(null);
  };

  /**
   * Start the ship. The request itself is unchanged; it runs in `@/lib/shipRun` so leaving this page
   * cannot cancel it. Where the operator goes is decided now (see @/lib/shipHandoff):
   *  - labels that print by themselves: stay here while it runs, then straight to the home screen.
   *    A failure stays here too, with the reason and Try Again — the form is still as entered.
   *  - otherwise: the Documents page at once, where the carrier label appears the moment it is
   *    bought and every other document as it is generated; a failure shows there, with a way back.
   */
  const submitOrderShip = () => {
    if (submittingRef.current || checkingOutcome) return;
    if (waitingHere) return;
    if (shipInFlight || shipDone) {
      navigate(shipDocumentsLocation(salesOrderId, activeBundle?.id));
      return;
    }
    submittingRef.current = true;
    setShipProblem(null);
    finishedRef.current = false;
    const buysCarrierLabel = isBundleShip || !isManualShip;
    const afterShip = afterShipFor({
      plan: shipAutoPrint,
      buysCarrierLabel,
      // A bundle's other members are not read here, so assume one of them carries serialized
      // product: a bundle goes home only when the SSCC label prints by itself too.
      expectsSscc: isBundleShip || (hasSerializedProducts && !salesOrder?.isDestructionOrder)
    });
    const goToDocuments = () => navigate(shipDocumentsLocation(salesOrderId, activeBundle?.id));
    const draft: ShipDraft = {
      packages,
      shippingMethodId,
      vendorShippingLocationId,
      fobShippingCost,
      fobTrackingNumber,
      freightWeight
    };
    const onSettled = (run: ShipRun, watched: boolean) => {
      // Listed on the home screen for a reprint, wherever the operator is by now.
      if (run.status === 'succeeded') {
        recordRecentShipment(vendorId as string, {
          orderId: run.orderId,
          label: run.label,
          orderCount: run.orderIds.length,
          printed: run.afterShip === 'home'
        });
      }
      // A screen showing the order says the outcome itself; this is for an operator who has moved on.
      if (watched) return;
      if (run.status === 'failed') {
        alert(
          `Could not ship ${run.label}`,
          run.outcomeUnknown
            ? 'The connection dropped before the answer came back. Open the order to check whether it shipped.'
            : run.error,
          8,
          'error'
        );
      } else if (run.afterShip === 'home') {
        const notice = shippedNotice(run, true);
        alert(notice.title, notice.message, SHIPPED_NOTICE_SECONDS, 'success');
      } else {
        alert(`${run.label} shipped`, 'Its documents are on the Documents page.', 4, 'success');
      }
    };
    if (isBundleShip) {
      // One set of packages for the COMBINED parcel, one label, then every member's own ship
      // transaction inside a single commit — see shipShippingBundle in libs/services.
      const started = startShipRun(
        {
          orderId: salesOrderId,
          orderIds: (activeBundle.orders || []).map((order) => order.id),
          kind: 'bundle',
          buysCarrierLabel: true,
          label: `Bundle ${activeBundle.bundleNumber}`,
          draft,
          afterShip
        },
        async () => {
          await ensureVendorShippingLocationOnOrder();
          await bundleShip({
            variables: {
              companyId: vendorId,
              shippingBundleId: activeBundle.id,
              shippingMethodId,
              packages: packages.map(packageShipInput)
            }
          });
        },
        onSettled
      );
      submittingRef.current = false;
      // Not started means a ship already covers one of these orders: show that one instead.
      if (!started) {
        alert('Already shipping', 'This shipment is already in progress.', 4, 'warning');
        goToDocuments();
      } else if (afterShip === 'documents') {
        goToDocuments();
      }
      return;
    }
    const variables = isFobOrder
      ? {
          id: salesOrderId,
          companyId: vendorId,
          // Money is stored in cents; the input is dollars.
          shippingAmount: Math.round(parseFloat(fobShippingCost || '0') * 100),
          trackingNumber: fobTrackingNumber.trim()
        }
      : isFreightOrder
      ? {
          id: salesOrderId,
          companyId: vendorId,
          // Freight keeps its real method on the order (pricing, documents, "ship via" label);
          // only the carrier LABEL is skipped. No packages — there is nothing to weigh per box.
          shippingMethodId: shippingMethod.id,
          // Money is stored in cents; the input is dollars.
          shippingAmount: Math.round(parseFloat(freightCostText || '0') * 100),
          trackingNumber: fobTrackingNumber.trim(),
          // Omitted when blank so "never weighed" stays distinct from a real 0 lbs.
          ...(freightWeightText
            ? { shippingWeight: parseFloat(freightWeightText) }
            : {})
        }
      : {
          packages: packages.map(packageShipInput),
          id: salesOrderId,
          shippingMethodId,
          companyId: vendorId
        };
    const started = startShipRun(
      {
        orderId: salesOrderId,
        orderIds: [salesOrderId],
        kind: 'order',
        buysCarrierLabel,
        label: `SO #${salesOrderNumber}`,
        draft,
        afterShip
      },
      async () => {
        await ensureVendorShippingLocationOnOrder();
        await orderShip({ variables });
      },
      onSettled
    );
    submittingRef.current = false;
    if (!started) {
      alert('Already shipping', 'This order is already being shipped.', 4, 'warning');
      goToDocuments();
    } else if (afterShip === 'documents') {
      goToDocuments();
    }
  };

  const shipStatus = (
    <ShipStatus
      ref={statusRef}
      running={waitingHere ? activeRun : undefined}
      problem={shipProblem}
      onCheckAgain={() =>
        setShipProblem((problem) => (problem ? { ...problem, checkTimedOut: false } : problem))
      }
    />
  );

  return (
    <Page className="sales-order-shipping action-page">
      <div className="action-top-bar">
        <div className="order-details">
          <h1>
            {isBundleShip ? `Bundle ${activeBundle.bundleNumber}` : `SO #${salesOrderNumber}`}
          </h1>
          {!isBundleShip && <h2>{totalQuantity} Products</h2>}
        </div>
        <FiXCircle onClick={returnToShipping} />
      </div>
      <div className="action-page-body">
        {(shipInFlight || shipDone) && !waitingHere && (
          <div className="ship-in-flight-notice" role="status">
            {shipInFlight && <FiLoader className="ship-in-flight-spinner" aria-hidden="true" />}
            <p>
              {shipInFlight
                ? `${activeRun.label} is shipping. Its labels and documents are coming in on the Documents page.`
                : `${activeRun.label} has shipped. Its labels and documents are on the Documents page.`}
            </p>
            <Button
              type="primary"
              fullWidth
              onClick={() => navigate(shipDocumentsLocation(salesOrderId, activeBundle?.id))}
            >
              View Documents
            </Button>
          </div>
        )}
        {vendorShippingLocations.length > 1 && hasSerializedProducts && (
          <Select
            form={false}
            label="Shipping Location (GCP)"
            value={vendorShippingLocationId}
            defaultValue={vendorShippingLocationId}
            onChange={handleGcpChange}
            disabled={gcpLoading || formLocked}
            options={vendorShippingLocations.map(({ id, gcp, address }) => [
              id,
              `${address?.city || ''}${
                address?.state ? ', ' + address.state : ''
              } - GCP: ${gcp}`
            ])}
          />
        )}
        {isManualShip ? (
          <>
            <p className="fob-notice">
              {isFreightOrder
                ? `Freight shipping${
                    shippingMethod?.name ? ` (${shippingMethod.name})` : ''
                  } — confirm or edit the freight cost (prefilled from the order), then enter the shipment weight and tracking / PRO number.`
                : 'FOB shipping — enter the shipping cost and the 3rd-party tracking number for this order.'}
            </p>
            {/* A number Input coerces an empty string to 0 in BOTH its blur and its Enter
                handlers, so an untouched field would report a real 0 — silently overwriting a
                freight order's quoted charge, and recording a 0 lb weight for a shipment that was
                never weighed. `manual` suppresses the blur path; `manual` does NOT cover Enter, so
                the caller-supplied onKeyDown below (caller props are spread last, so ours wins)
                closes it. Typing is unaffected — onInput is gated by neither — so the field only
                ever holds a value the operator actually entered, and blank stays genuinely blank. */}
            <Input
              form={false}
              manual
              disabled={formLocked}
              onKeyDown={suppressEnterCommit}
              label="Shipping Cost ($)"
              inputType="number"
              value={fobShippingCost}
              onChange={({ value }) => setFobShippingCost(value)}
            />
            {isFreightOrder && (
              <Input
                form={false}
                manual
                disabled={formLocked}
                onKeyDown={suppressEnterCommit}
                label="Weight (lbs)"
                inputType="number"
                value={freightWeight}
                onChange={({ value }) => setFreightWeight(value)}
              />
            )}
            <Input
              form={false}
              disabled={formLocked}
              label={isFreightOrder ? 'Tracking / PRO Number' : 'Tracking Number'}
              value={fobTrackingNumber}
              onChange={({ value }) => setFobTrackingNumber(value)}
            />
            {bundledButManual && (
              <p className="fob-notice bundle-conflict-notice">
                This order is in shipping bundle {activeBundle.bundleNumber}, but its shipping
                method no longer buys a carrier label. Go back and remove it from the bundle to
                ship it on its own.
              </p>
            )}
            <div className="ship-action">
              {shipStatus}
              <Button
                type="primary"
                fullWidth
                onClick={submitOrderShip}
                iconBefore={waitingHere ? <FiLoader className="ship-in-flight-spinner" /> : undefined}
                disabled={
                  !fobTrackingNumber.trim() ||
                  // Freight orders are placed with a QUOTED shipping charge, so a blank cost here
                  // would submit $0 and overwrite it. An explicit 0 is allowed; empty is not.
                  (isFreightOrder && !freightCostText) ||
                  formLocked ||
                  bundledButManual
                }
              >
                {waitingHere ? 'Shipping…' : shipProblem ? 'Try Again' : 'Ship'}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className={['packages', formLocked && 'is-locked'].filter(Boolean).join(' ')}>
              {isBundleShip && (
                <div className="bundle-ship-summary">
                  <div className="bundle-ship-kicker">
                    <RiArchiveStackLine aria-hidden="true" />
                    <span>Bundle {activeBundle.bundleNumber}</span>
                    <span className="bundle-ship-count">
                      {bundleOrderCount === 1
                        ? '1 order'
                        : `${bundleOrderCount} orders`}
                      , one parcel
                    </span>
                  </div>
                  <p>
                    Enter packages for the <strong>whole bundle</strong>. The
                    shipping method you select is saved on every order below, and
                    they all travel under the labels generated here.
                  </p>
                  <ul className="bundle-ship-orders">
                    {(activeBundle.orders || []).map((order) => (
                      <li key={order.id}>
                        <span className="so-number">
                          SO #{order.vendorSalesOrderNumber}
                        </span>
                        <span className="so-method">
                          {order.vendorStatus === 'picking'
                            ? 'Still at Pick / Verify'
                            : order.shippingMethodName || 'FOB'}
                        </span>
                        <span className="row-count">
                          {order.itemCount === 1
                            ? '1 item'
                            : `${order.itemCount} items`}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {bundleNotReady.length > 0 && (
                    <p className="fob-notice bundle-conflict-notice" role="status">
                      {bundleNotReady.length === 1
                        ? `SO #${bundleNotReady[0].vendorSalesOrderNumber} is`
                        : `${bundleNotReady.length} orders are`}{' '}
                      in this bundle but not verified yet. The bundle ships once every order
                      in it is verified. To ship without{' '}
                      {bundleNotReady.length === 1 ? 'it' : 'them'}, go back and remove{' '}
                      {bundleNotReady.length === 1 ? 'it' : 'them'} from the bundle.
                    </p>
                  )}
                </div>
              )}
              {packages.map((pkg, i) => {
                const dimensions = packingDimensions(pkg);
                return (
                  <div
                    className={`package${dimensions ? '' : ' needs-size'}`}
                    key={i}
                    onClick={() => openPackage(i)}
                  >
                    <div className="left">
                      <span className="package-type">
                        {pkg.type === 'box' ? <FiBox /> : <FiShoppingBag />} {pkg.type}
                      </span>
                      <br />
                      {dimensions ? (
                        <span className="package-size-line">
                          {formatDimensions(dimensions)}
                        </span>
                      ) : (
                        <span className="package-size-missing">
                          {pkg.sizeMode === 'custom'
                            ? 'New size not saved yet — tap to finish'
                            : 'Needs a size — tap to enter'}
                        </span>
                      )}
                      <br /> {pkg.weight} lbs
                    </div>
                    <div className="right">
                      <FiTrash
                        className="delete"
                        aria-label={`Remove package ${i + 1}`}
                        onClick={(e) => {
                          // The card itself opens the package; removing it must not.
                          e.stopPropagation();
                          if (formLocked) return;
                          setPackages(packages.filter((_, j) => i !== j));
                        }}
                      />
                    </div>
                  </div>
                );
              })}
              <Button
                type="secondary"
                fullWidth
                iconBefore={<FiPlus />}
                disabled={formLocked}
                onClick={() => {
                  if (formLocked) return;
                  const selectedIndex = packages.length;
                  setPackages([
                    ...packages,
                    newPackage(dimensionOptions, packages[packages.length - 1])
                  ]);
                  openPackage(selectedIndex);
                }}
              >
                Add a package
              </Button>
            </div>
            <Select
              form={false}
              label="Shipping Method"
              disabled={formLocked}
              value={shippingMethodId}
              defaultValue={shippingMethodId}
              onChange={({ value }) => setShippingMethodId(value)}
              // Freight methods are excluded: this Select drives the carrier-LABEL path, and a
              // freight method has no carrier account to buy one from. Picking one here would
              // otherwise submit packages for an order the server dispatches as freight.
              options={shippingMethods
                .filter(({ carrier }) => carrier !== 'freight')
                .map(({ id, name }) => [id, name])}
            />
            {packagesWithoutSize.length > 0 && (
              <p className="fob-notice package-size-blocker" role="status">
                {packagesWithoutSize.length === 1
                  ? `Package ${packagesWithoutSize[0]} needs`
                  : `Packages ${packagesWithoutSize.slice(0, -1).join(', ')} and ${
                      packagesWithoutSize[packagesWithoutSize.length - 1]
                    } need`}{' '}
                a size before labels can be generated.
              </p>
            )}
            <div className="ship-action">
              {shipStatus}
              <Button
                type="primary"
                fullWidth
                iconBefore={
                  waitingHere ? (
                    <FiLoader className="ship-in-flight-spinner" />
                  ) : isBundleShip ? (
                    <RiArchiveStackLine />
                  ) : undefined
                }
                onClick={submitOrderShip}
                disabled={
                  !packages.length ||
                  packagesWithoutSize.length > 0 ||
                  !shippingMethodId ||
                  formLocked ||
                  // A member combined at Pick / Verify is not verified yet (notice above).
                  bundleNotReady.length > 0
                }
              >
                {waitingHere
                  ? 'Generating Labels…'
                  : shipProblem
                  ? 'Try Again'
                  : isBundleShip
                  ? `Ship Bundle · ${bundleOrderCount} ${bundleOrderCount === 1 ? 'order' : 'orders'}`
                  : 'Generate Labels'}
              </Button>
            </div>
          </>
        )}
      </div>

      <Modal
        className="shipping-package-modal"
        modalOpen={!!selectedPackage}
        setModalOpen={closePackage}
      >
        <div className="package-sheet__heading">Package type</div>
        <Radio
          form={false}
          options={[
            ['box', 'Box'],
            ['bag', 'Poly Bag']
          ]}
          value={type}
          defaultValue={type}
          onChange={({ value }) =>
            setPackages(
              packages.map((pkg, j) =>
                j === selectedPackageIndex
                  ? changePackageType(pkg, value, dimensionOptions)
                  : pkg
              )
            )
          }
        />
        {selectedPackage && (
          <PackageSizeField
            // A fresh field per package and type: touched state and the size Select's own
            // text must not carry over from the last package opened.
            key={`${selectedPackageIndex}-${type}`}
            pkg={selectedPackage}
            savedSizes={savedSizesFor(dimensionOptions, type)}
            onChange={(patch) => {
              setAddSizeError(null);
              updatePackage(patch);
            }}
            showAllErrors={sizeErrorsShown}
            onSubmit={savePackage}
            addsToLibrary
            addError={addSizeError}
          />
        )}
        <div className="package-sheet__heading">Weight</div>
        <Input
          form={false}
          label="Weight in Pounds"
          inputType="number"
          value={weight}
          onChange={({ value }) => {
            if (value === weight) return;
            updatePackage({ weight: value, weightEntered: true });
          }}
        />
        {laneScaleActive && (
          <p className="scale-hint">
            {weightEntered ? (
              <>
                Entered by hand.{' '}
                <button
                  type="button"
                  className="scale-hint-action"
                  onClick={() => updatePackage({ weightEntered: false, weightKept: false })}
                >
                  Use {laneScaleName}
                </button>
              </>
            ) : weightKept ? (
              <>
                Weighed on {laneScaleName}.{' '}
                <button
                  type="button"
                  className="scale-hint-action"
                  onClick={() => updatePackage({ weightKept: false })}
                >
                  Weigh again
                </button>
              </>
            ) : !laneScale.reported ? (
              `Reading ${laneScaleName}…`
            ) : laneScale.pounds == null ? (
              `${laneScaleName} not reporting`
            ) : (
              `From ${laneScaleName}`
            )}
          </p>
        )}
        {/* <Select
          form={false}
          label="Location"
          options={locations.map(({ id, name }) => [id, name])}
          value={selectedLocationId}
          // onChange={({ value }) => setSelectedLocationId(value)}
        /> */}
        <Button type="primary" fullWidth onClick={savePackage} disabled={addingSize}>
          {addingSize ? 'Saving size…' : 'Save'}
        </Button>
      </Modal>
    </Page>
  );
};

export default SalesOrderShipping;
